import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { Link, routerShape, withRouter } from 'react-router';
import PropTypes from 'prop-types';
import b from 'b_';
import {
    saasIsolationLicense,
    tapAndSaaSIsolationLicense,
    tapIsolationLicense
} from '../../productLicense';
import ConsoleNavigationStore from '../../stores/ConsoleNavigationStore';
import ConsoleStore, { POLICY_MENU_DISPLAYS } from '../../stores/ConsoleStore';
import Accordion from '../Accordion/Accordion';
import Icon from '../Icon/Icon';
import FuseButton from '../FuseControls/FuseButton';
import './SideNavigation.css';

const block = b.lock('SideNavigation');

class SideNavigation extends Component {
    static LEGACY_STATUSES = {
        LEGACY: 'Legacy',
        PRIORITIZED: 'Prioritized'
    }
    static propTypes = {
        console: ConsoleStore.PropType.isRequired,
        consoleNavigation: ConsoleNavigationStore.PropType.isRequired,
        // Location is not "required" since we don't use the router in storybook
        location: PropTypes.shape({ pathname: PropTypes.string.isRequired }),
        router: routerShape
    };

    state = {
        focus: false,
    };

    setFocus = (focus) => this.setState({ focus });

    refreshFocusState = () => this.setFocus(this.props.consoleNavigation.pinned.isTrue);

    onPinClick = () => {
        this.props.consoleNavigation.pinned.toggle();
        setTimeout(this.refreshFocusState);
    };

    renderLink(to, text, icon = undefined, { fade, status } = {}) {
        const sub = !icon;
        return <Link
            key={to}
            className={block('ItemWrapper', { sub })}
            activeClassName={block('ItemWrapper', { active: true })}
            to={to}
            onClick={this.refreshFocusState}
            tabIndex="0"
        >
            <div
                key={to}
                className={block('Item', { sub, fade, link: true })}
                tabIndex="-1"
                data-qa={to.replace(/\//g, '').replace('console', '')}
            >
                {sub ? text : <Fragment>
                    <Icon className={block('ItemIcon')} type={icon} />
                    <div className={block('ItemLabel')}>
                        {text} {status ? this.renderMenuStatus(status) : null}
                    </div>
                </Fragment>}
            </div>
        </Link>;
    }

    renderMenuStatus = (status) =>
        <span className={block('LegacyMarker', { status })}>{status}</span>

    isSubGroupActive = (subgroup, links) => {
        const subgroupItems = links.filter((link) => subgroup.id === link.subGroupId);
        const result = subgroupItems.some((link) => this.props.router?.isActive(link.to, false));
        return result;
    }

    renderSubGroup = (link, links) => {
        const active = this.isSubGroupActive(link, links);
        return <div key={'subgroup' + link.id} className={block('SubGroupTitle', { active })}>{link.text}</div>;
    }

    renderMenuGroup(title, icon, flagStore, links, status = undefined) {
        const active = links.some((link) => link && this.props.location && link.to === this.props.location?.pathname);
        return <Fragment>
            <Accordion
                className={block('Accordion', { active, expanded: flagStore.isTrue })}
                flagStore={flagStore}
                contentClassName={block('AccordionContent')}
                labelWrapperClassName={block('ItemWrapper')}
                labelClassName={block('Item')}
                iconClassName={block('ItemIcon')}
                onClick={this.refreshFocusState}
                label={<Fragment>
                    <Icon className={block('ItemIcon')} type={icon} />
                    <div className={block('ItemLabel')}>
                        {title} {status ? this.renderMenuStatus(status) : null}
                    </div>
                </Fragment>}
            >
                {links.filter((link) => !!link).map((link) => (link.isSubgroup
                    ? this.renderSubGroup(link, links)
                    : this.renderLink(
                        link.to,
                        link.text,
                        undefined,
                        { fade: link.fade }
                    ))
                )}
            </Accordion>
        </Fragment>;
    }

    renderAltPolicies = () => {
        const { console, consoleNavigation } = this.props;

        const hasBrowserEmailIso = console.hasBrowserOrEmailLicense();
        const hasBrowserIsoWithUrlFlagOrTap = (hasBrowserEmailIso && console.urlIsolationProductEnabled)
            || console.hasTapUrlIsolationLicense();
        const isAdmin = console.isConsoleAdmin;
        const links = [
            { to: '/console/policies/overview', text: 'Overview' },
            { isSubgroup: true, text: 'Rules', id: 1 },
            { to: '/console/policies/exclusion_rules', text: 'Exclusion Rules', subGroupId: 1 }
        ];
        if (hasBrowserIsoWithUrlFlagOrTap || isAdmin) {
            links.push({
                to: '/console/policies/redirect_rules',
                text: 'Redirect Rules',
                subGroupId: 1,
                fade: !hasBrowserIsoWithUrlFlagOrTap
            });
        }
        links.push({ to: '/console/policies/content_rules', text: 'Content Rules', subGroupId: 1 });
        if (console.isFTREnabled()) {
            links.push({
                to: '/console/policies/file_threat_rules',
                text: 'File Threat Rules',
                subGroupId: 1,
            });
        }
        if (console.isDLPEnabled()) {
            links.push({
                to: '/console/policies/dlp_rules',
                text: 'File DLP Rules',
                subGroupId: 1,
            });
        }
        links.push(
            { isSubgroup: true, text: 'Match Criteria', id: 2 },
            { to: '/console/policies/browsing_roles', text: 'Browsing Roles', subGroupId: 2 },
            { to: '/console/policies/custom_categories', text: 'Custom Categories', subGroupId: 2 },
            { isSubgroup: true, text: 'Tools', id: 3 },
            { to: '/console/policies/policy_testing', text: 'Policy Testing', subGroupId: 3 });

        let status;
        if (console.policyMenuDisplay === POLICY_MENU_DISPLAYS.OLD_AND_NEW) {
            status = console.useRedesignedPolicy
                ? SideNavigation.LEGACY_STATUSES.PRIORITIZED
                : SideNavigation.LEGACY_STATUSES.LEGACY;
        }
        return this.renderMenuGroup(
            (console.policyMenuDisplay === POLICY_MENU_DISPLAYS.OLD_AND_NEW ? 'New ' : '') + 'Policies',
            'policy',
            consoleNavigation.newPoliciesToggle,
            links,
            status
        );
    }

    hasBrowserIso = () => {
        const { console } = this.props;
        return console.license !== tapIsolationLicense
            && console.license !== tapAndSaaSIsolationLicense
            && console.license !== saasIsolationLicense;
    }

    renderPolicies = () => {
        const { console, consoleNavigation } = this.props;
        const isAdmin = console.isConsoleAdmin;
        const defaultProduct = console.onlyWebmail ? 'Email Isolation' : 'Browser Isolation';
        // This is blasphemy! This is madness!
        // Madness? This is Sparta!
        const hasSaasIso = console.saasIsolationProductEnabled;
        const hasBrowserIso = this.hasBrowserIso();
        const hasUrlIso = console.hasUrlIsolation();

        let status;
        if (console.policyMenuDisplay === POLICY_MENU_DISPLAYS.OLD_AND_NEW) {
            status = console.useRedesignedPolicy
                ? SideNavigation.LEGACY_STATUSES.LEGACY
                : SideNavigation.LEGACY_STATUSES.PRIORITIZED;
        }
        return (hasBrowserIso && hasUrlIso) || isAdmin ? <Fragment>{this.renderMenuGroup(
            'Policies',
            'policy',
            consoleNavigation.policiesToggle,
            [
                { to: '/console/policies', text: defaultProduct, fade: !hasBrowserIso },
                { to: '/console/url_isolation_policies', text: 'URL Isolation', fade: !hasUrlIso },
            ],
            status
        )}<div className={block('Separator')} />
        </Fragment> : <Fragment>
            {hasBrowserIso ?
                <Fragment>
                    {this.renderLink('/console/policies', 'Policy', hasSaasIso ? 'web' : 'policy', { status })}
                    <div className={block('Separator')} />
                </Fragment> : null}
            {hasUrlIso ?
                <Fragment>
                    {this.renderLink('/console/url_isolation_policies', 'Policy', 'policy', { status })}
                    <div className={block('Separator')} />
                </Fragment> : null}
        </Fragment>;
    }

    render() {
        const { console, consoleNavigation } = this.props;
        const isAdmin = console.isConsoleAdmin;
        const { publicApiV3Enabled, allowConsoleApi } = console;
        const defaultProduct = console.onlyWebmail ? 'Email Isolation' : 'Browser Isolation';
        const hasBrowserIso = console.hasBrowserOrEmailLicense();
        const hasTapUrlIsoLicense = console.hasTapUrlIsolationLicense();
        const hasUrlIso = console.hasUrlIsolation();
        const customizeUIEnabled = console.isMirrorFeatureEnabled('CUSTOMIZE_UI');

        return <div
            className={block({
                pinned: consoleNavigation.pinned.isTrue,
                focus: this.state.focus,
            })}
            onFocus={() => this.setFocus(true)}
            onBlur={() => this.setFocus(false)}
        >
            <div className={block('Wrapper')}>
                {hasBrowserIso || hasUrlIso || isAdmin ? <Fragment>
                    {(hasBrowserIso && hasUrlIso) || isAdmin ? this.renderMenuGroup(
                        'Dashboards',
                        'dashboard',
                        consoleNavigation.dashboardToggle,
                        [
                            { to: '/console/dashboards/browser_isolation', text: defaultProduct, fade: !hasBrowserIso },
                            { to: '/console/dashboards/url_isolation', text: 'URL Isolation', fade: !hasUrlIso },
                        ],
                    ) : <Fragment>
                        {hasBrowserIso ?
                            this.renderLink('/console/dashboards/browser_isolation', 'Dashboard', 'dashboard') : null}
                        {hasUrlIso ?
                            this.renderLink('/console/dashboards/url_isolation', 'Dashboard', 'dashboard') : null}
                    </Fragment>}
                    <div className={block('Separator')} />
                </Fragment> : null}

                {console.menuHasNew() ? <Fragment>
                    {this.renderAltPolicies()}
                    <div className={block('Separator')} />
                </Fragment> : null}

                {console.menuHasOld() ? this.renderPolicies() : null}

                {hasBrowserIso || hasTapUrlIsoLicense || isAdmin ? this.renderMenuGroup(
                    'Product Settings',
                    'settings',
                    consoleNavigation.productSettingsToggle,
                    [
                        publicApiV3Enabled && allowConsoleApi ? {
                            to: '/console/product_settings/policy_api',
                            text: 'Policy API',
                        } : null,
                        hasBrowserIso || hasTapUrlIsoLicense || isAdmin ? {
                            to: '/console/product_settings/reporting_api',
                            text: 'Reporting API',
                            fade: !hasBrowserIso && !hasTapUrlIsoLicense
                        } : null,
                        (hasBrowserIso || isAdmin) && !console.useGeneralOptions() ? {
                            to: '/console/registration_options',
                            text: 'Registration Options',
                            fade: !hasBrowserIso,
                        } : null,
                        (hasBrowserIso || isAdmin) && console.useGeneralOptions() ? {
                            to: '/console/product_settings/general_options',
                            text: 'General Options',
                            fade: !hasBrowserIso,
                        } : null,
                        {
                            to: '/console/product_settings/saml_sso_configuration',
                            text: 'SAML/SSO Configuration',
                        },
                        ...console.showPolicyMigrationMenu ? [{
                            to: '/console/product_settings/new_policies_migration',
                            text: 'New Policies Migration',
                        }] : []
                    ],
                ) : null}

                {customizeUIEnabled ? this.renderLink(
                    '/console/customize_ui',
                    'Customize UI',
                    'palette',
                ) : this.renderMenuGroup(
                    'Customize UI',
                    'palette',
                    consoleNavigation.customizeUIToggle,
                    [
                        hasBrowserIso || hasTapUrlIsoLicense || isAdmin ? {
                            to: '/console/customize_ui/browser_bar',
                            text: 'Browser Bar',
                            fade: !(hasBrowserIso || hasTapUrlIsoLicense),
                        } : null,
                        console.menuHasNew() ? {
                            to: '/console/customize_ui/exit_message',
                            text: 'Exit Message',
                        } : null,
                        { to: '/console/customize_ui/block_pages', text: 'Block Pages' },
                        hasBrowserIso || isAdmin ? {
                            to: '/console/customize_ui/registration_notice',
                            text: 'Registration Notice',
                            fade: !hasBrowserIso,
                        } : null,
                    ],
                )}

                {this.renderMenuGroup(
                    'Administrators',
                    'supervised_user_circle',
                    consoleNavigation.administratorsToggle,
                    [
                        { to: '/console/administrators/local_accounts', text: 'Local Accounts' },
                        { to: '/console/administrators/saml_sso_access', text: 'SAML/SSO Access' },
                    ],
                )}

                {this.renderLink('/console/audit_log', 'Audit Log', 'history')}
            </div>
            <div className={block('PinWrapper')}>
                <FuseButton
                    className={block('Pin')}
                    icon={consoleNavigation.pinned.isTrue ? 'arrow_back' : 'push_pin'}
                    onClick={this.onPinClick}
                    invertBackgroundColor
                />
            </div>
        </div>;
    }
}

export default withRouter(inject('console', 'consoleNavigation')(observer(SideNavigation)));
